import { useEffect, useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  UserRound,
  LogOut,
  Inbox,
  Bookmark,
  Truck,
  Save,
  Star,
  Sun,
  Moon,
  Rows3,
  Rows4,
} from 'lucide-react';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Pill, type PillVariant } from '../components/ui/pill.js';
import { Feedback } from '../components/ui/feedback.js';
import { cn } from '../lib/cn.js';
import { useAuth } from '../lib/auth-store.js';
import { authMode, signOutCognito } from '../lib/cognito-client.js';
import {
  api,
  type ActivityEvent,
  type AppUser,
  type UserDensity,
  type UserRole,
  type UserTheme,
} from '../lib/api.js';
import { countPending, getOutboxDb } from '../lib/outbox.js';
import { applyDensity, applyTheme } from '../lib/theme.js';

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  office: 'Office',
  field_tech: 'Field tech',
};

const ROLE_VARIANT: Record<UserRole, PillVariant> = {
  admin: 'install',
  dispatcher: 'service',
  office: 'pm',
  field_tech: 'sales',
};

/**
 * /me — personal settings page.
 *
 * Profile fields (displayName, defaultWarehouse) are user-editable via
 * PATCH /api/v1/me. Email and role are read-only — those live behind the
 * admin Users panel. While AUTH_DISABLED is set, "current user" resolves
 * to DEV_USER_EMAIL or the first admin in the DB.
 */
export function MeScreen() {
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    staleTime: 30_000,
  });

  // Apply the server's theme + density whenever /me re-resolves. Bootstrap
  // already pulled the last-known values from localStorage on app load;
  // this keeps them honest with what the server says.
  useEffect(() => {
    if (!meQuery.data) return;
    applyTheme(meQuery.data.theme);
    applyDensity(meQuery.data.density);
  }, [meQuery.data]);

  if (meQuery.isPending) return <Skeleton />;
  if (meQuery.isError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Me</h1>
        <Feedback variant="error" size="lg">
          Couldn't load your profile: {meQuery.error.message}
        </Feedback>
      </div>
    );
  }

  return <Body user={meQuery.data} />;
}

function Body({ user }: { user: AppUser }) {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Me</h1>

      <ProfileCard user={user} />

      <PreferencesCard user={user} />

      <ActivityCard />

      {user.techId && <TruckCard />}

      <div className="grid gap-3 md:grid-cols-2">
        <BookmarksCard user={user} />
        <OutboxCard />
      </div>

      <SignOutCard />
    </div>
  );
}

function ProfileCard({ user }: { user: AppUser }) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [defaultWarehouse, setDefaultWarehouse] = useState(user.defaultWarehouse ?? '');
  const [savedFlash, setSavedFlash] = useState(false);

  // Keep form in sync if the server-side data changes (e.g. admin renamed me).
  useEffect(() => {
    setDisplayName(user.displayName);
    setDefaultWarehouse(user.defaultWarehouse ?? '');
  }, [user.displayName, user.defaultWarehouse]);

  const updateMutation = useMutation({
    mutationFn: (body: { displayName?: string; defaultWarehouse?: string | null }) =>
      api.me.update(body),
    onSuccess: (next) => {
      queryClient.setQueryData(['me'], next);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    },
  });

  const dirty =
    displayName.trim() !== user.displayName ||
    (defaultWarehouse || null) !== (user.defaultWarehouse ?? null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!dirty || updateMutation.isPending) return;
    const patch: { displayName?: string; defaultWarehouse?: string | null } = {};
    if (displayName.trim() !== user.displayName) patch.displayName = displayName.trim();
    if ((defaultWarehouse || null) !== (user.defaultWarehouse ?? null)) {
      patch.defaultWarehouse = defaultWarehouse || null;
    }
    updateMutation.mutate(patch);
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border bg-surface p-5 space-y-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <header className="flex items-start gap-4">
        <Avatar name={user.displayName} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold text-ink truncate">{user.displayName}</h2>
            <Pill variant={ROLE_VARIANT[user.role]}>{ROLE_LABEL[user.role]}</Pill>
            {user.active ? (
              <Pill variant="success" dot>Active</Pill>
            ) : (
              <Pill variant="neutral">Deactivated</Pill>
            )}
          </div>
          <div className="text-sm text-ink-muted font-mono">{user.email}</div>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
        <label className="block text-sm space-y-1">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Display name</span>
          <Input
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
          />
        </label>
        <label className="block text-sm space-y-1">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Default whse</span>
          <Input
            value={defaultWarehouse}
            onChange={(e) => setDefaultWarehouse(e.target.value.trim().toUpperCase())}
            placeholder="00"
            className="font-mono"
            autoCapitalize="characters"
          />
        </label>
      </div>

      <div className="text-xs text-ink-muted">
        Email and role are admin-managed — see <span className="font-mono">/admin → Users</span> to change them.
      </div>

      {updateMutation.isError && (
        <Feedback variant="error">
          Save failed: {updateMutation.error.message}
        </Feedback>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-emerald-700" aria-live="polite">
          {savedFlash ? 'Saved.' : ''}
        </span>
        <Button type="submit" size="sm" disabled={!dirty || updateMutation.isPending}>
          <Save className="h-3.5 w-3.5" aria-hidden />
          {updateMutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

function ActivityCard() {
  const query = useQuery({
    queryKey: ['me', 'activity'],
    queryFn: ({ signal }) => api.me.activity(20, signal),
    staleTime: 30_000,
  });
  return (
    <div
      className="rounded-lg border bg-surface p-5"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <header className="flex items-center justify-between gap-2 mb-3">
        <div>
          <div className="text-sm font-medium text-ink">Recent activity</div>
          <div className="text-xs text-ink-muted">
            Server-logged on dispatch assigns, parts capture, signature attach, and photo upload.
          </div>
        </div>
        <span className="text-[10px] text-ink-subtle">Last {query.data?.length ?? 0}</span>
      </header>
      {query.isPending && (
        <ul className="space-y-1.5" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="h-7 bg-surface-muted rounded animate-pulse" />
          ))}
        </ul>
      )}
      {query.isError && (
        <div className="text-xs text-red-700">Couldn't load activity: {query.error.message}</div>
      )}
      {query.data && query.data.length === 0 && (
        <div className="text-sm text-ink-muted">
          No activity yet. Drop a job onto the dispatch board, capture a part on a service order,
          or attach a signature — events show up here.
        </div>
      )}
      {query.data && query.data.length > 0 && (
        <ul className="space-y-1.5">
          {query.data.map((ev) => (
            <ActivityRow key={ev.id} ev={ev} />
          ))}
        </ul>
      )}
    </div>
  );
}

const ACTIVITY_TONE: Record<string, PillVariant> = {
  'dispatch.assign': 'info',
  'job.create_field': 'info',
  'parts.capture': 'install',
  'photo.upload': 'pm',
  'signature.attach': 'sales',
};

function ActivityRow({ ev }: { ev: ActivityEvent }) {
  const tone = ACTIVITY_TONE[ev.kind] ?? 'neutral';
  return (
    <li className="grid grid-cols-[auto_1fr_auto] gap-2 items-center text-sm">
      <Pill variant={tone}>{kindLabel(ev.kind)}</Pill>
      <span className="text-ink truncate">{ev.summary}</span>
      <span className="font-mono text-[10px] text-ink-subtle">{relative(ev.occurredAt)}</span>
    </li>
  );
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'dispatch.assign': return 'Dispatch';
    case 'job.create_field': return 'New job';
    case 'parts.capture': return 'Parts';
    case 'photo.upload': return 'Photo';
    case 'signature.attach': return 'Sig';
    default: return kind;
  }
}

function relative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function PreferencesCard({ user }: { user: AppUser }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (patch: { theme?: UserTheme | null; density?: UserDensity | null }) =>
      api.me.update(patch),
    onSuccess: (next) => {
      queryClient.setQueryData(['me'], next);
      applyTheme(next.theme);
      applyDensity(next.density);
    },
  });

  // Apply optimistically as well so the toggle feels instant.
  const setTheme = (theme: UserTheme | null) => {
    applyTheme(theme);
    mutation.mutate({ theme });
  };
  const setDensity = (density: UserDensity | null) => {
    applyDensity(density);
    mutation.mutate({ density });
  };

  return (
    <div
      className="rounded-lg border bg-surface p-5 space-y-3"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <header className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-ink">Preferences</div>
          <div className="text-xs text-ink-muted">Theme + density sync across your devices.</div>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">Theme</div>
          <div
            className="inline-flex items-stretch rounded-md border bg-surface overflow-hidden"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
            role="group"
            aria-label="Theme"
          >
            <ToggleButton active={user.theme === 'light' || user.theme === null} onClick={() => setTheme('light')}>
              <Sun className="h-3.5 w-3.5" aria-hidden /> Light
            </ToggleButton>
            <ToggleButton active={user.theme === 'dark'} onClick={() => setTheme('dark')}>
              <Moon className="h-3.5 w-3.5" aria-hidden /> Dark
            </ToggleButton>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">Density</div>
          <div
            className="inline-flex items-stretch rounded-md border bg-surface overflow-hidden"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
            role="group"
            aria-label="Density"
          >
            <ToggleButton
              active={user.density === 'comfortable' || user.density === null}
              onClick={() => setDensity('comfortable')}
            >
              <Rows3 className="h-3.5 w-3.5" aria-hidden /> Comfortable
            </ToggleButton>
            <ToggleButton active={user.density === 'compact'} onClick={() => setDensity('compact')}>
              <Rows4 className="h-3.5 w-3.5" aria-hidden /> Compact
            </ToggleButton>
          </div>
        </div>
      </div>

      {mutation.isError && (
        <Feedback variant="error" size="sm">
          Save failed: {mutation.error.message}
        </Feedback>
      )}
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'bg-brand-50 text-ink-accent'
          : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function BookmarksCard({ user }: { user: AppUser }) {
  const queryClient = useQueryClient();
  const bookmarks = user.bookmarkedParts;

  const mutation = useMutation({
    mutationFn: (next: string[]) => api.me.update({ bookmarkedParts: next }),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: ['me'] });
      const previous = queryClient.getQueryData<AppUser>(['me']);
      if (previous) queryClient.setQueryData(['me'], { ...previous, bookmarkedParts: next });
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      const previous = (ctx as { previous?: AppUser } | undefined)?.previous;
      if (previous) queryClient.setQueryData(['me'], previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const remove = (partNo: string) => {
    mutation.mutate(bookmarks.filter((p) => p !== partNo));
  };

  return (
    <div
      className="rounded-lg border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-ink">
          <Bookmark className="h-4 w-4 text-ink-muted" aria-hidden />
          Bookmarked parts
        </div>
        <span className="text-[10px] text-ink-subtle">{bookmarks.length} saved</span>
      </div>
      {bookmarks.length === 0 ? (
        <div className="text-xs text-ink-muted">
          Tap the star on an inventory detail page to bookmark a part for quick lookup later.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {bookmarks.map((partNo) => (
            <li
              key={partNo}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <Link
                to="/inventory"
                search={{ q: partNo }}
                className="inline-flex items-center gap-1.5 min-w-0 hover:text-ink-accent"
              >
                <Star className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--color-text-info)' }} fill="currentColor" aria-hidden />
                <span className="font-mono truncate">{partNo}</span>
              </Link>
              <button
                type="button"
                onClick={() => remove(partNo)}
                aria-label={`Remove ${partNo} bookmark`}
                className="text-[11px] text-ink-subtle hover:text-red-700"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Entry to My truck — only for users linked to a tech (the screen itself explains a missing warehouse). */
function TruckCard() {
  return (
    <Link
      to="/truck"
      className="rounded-lg border bg-surface p-4 block transition-shadow hover:shadow-sm"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-ink mb-1">
        <Truck className="h-4 w-4 text-ink-muted" aria-hidden />
        My truck
      </div>
      <p className="text-xs text-ink-muted">
        What's on your van right now, restock transfers, and truck counts.
      </p>
    </Link>
  );
}

function OutboxCard() {
  const pending = useLiveQuery(() => countPending(getOutboxDb()), [], 0);
  return (
    <Link
      to="/outbox"
      className="rounded-lg border bg-surface p-4 block transition-shadow hover:shadow-sm"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 text-sm font-medium text-ink">
          <Inbox className="h-4 w-4 text-ink-muted" aria-hidden />
          Outbox
        </div>
        {pending > 0 ? (
          <Pill variant="warning">{pending} pending</Pill>
        ) : (
          <Pill variant="success" dot>Clear</Pill>
        )}
      </div>
      <p className="text-xs text-ink-muted">
        Drafts and quotes queued offline. They submit to Spire automatically once a connection is
        available.
      </p>
    </Link>
  );
}

function SignOutCard() {
  const queryClient = useQueryClient();
  const clearAuth = useAuth((s) => s.clear);
  const signOut = () => {
    if (!window.confirm('Sign out?')) return;
    clearAuth();
    queryClient.clear();
    if (authMode === 'cognito') {
      // Drop the Cognito SDK's cached session (idToken + refreshToken
      // in localStorage under CognitoIdentityServiceProvider.*).
      signOutCognito();
    }
    // Hard-redirect so the router-guard re-evaluates and the new
    // localStorage state is honored from a clean React root.
    window.location.assign('/login');
  };

  return (
    <div
      className="rounded-lg border bg-surface p-4 flex items-center gap-3"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <LogOut className="h-5 w-5 text-ink-muted shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-ink">Sign out</div>
        <div className="text-xs text-ink-muted">
          Clears your JWT + cached state and returns to the login picker.
        </div>
      </div>
      <Button type="button" size="sm" variant="ghost" onClick={signOut}>
        Sign out
      </Button>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initials =
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  return (
    <span
      aria-hidden
      className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold"
      style={{ backgroundColor: 'var(--pill-service-bg)', color: 'var(--pill-service-fg)' }}
    >
      {initials || <UserRound className="h-6 w-6" />}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="h-7 w-20 bg-surface-muted rounded animate-pulse" />
      <div className="h-32 bg-surface-muted rounded animate-pulse" />
      <div className="grid grid-cols-2 gap-3">
        <div className="h-24 bg-surface-muted rounded animate-pulse" />
        <div className="h-24 bg-surface-muted rounded animate-pulse" />
      </div>
    </div>
  );
}

